<template>
  <div class="ai-config-simple">
    <div class="page-header">
      <div>
        <h2>🤖 AI智能助手配置概览</h2>
        <p>管理员统一维护AI能力，这里为您展示当前账套真实的AI接口与提示词配置</p>
      </div>
      <div class="header-actions">
        <button class="refresh-btn" :disabled="isRefreshing" @click="refreshAll">
          {{ isRefreshing ? '刷新中...' : '🔄 刷新数据' }}
        </button>
      </div>
    </div>

    <div class="status-grid">
      <div class="status-card">
        <div class="card-title">
          <span>AI服务状态</span>
          <span class="status-pill" :class="aiConnected ? 'online' : 'offline'">
            {{ aiConnected ? '已就绪' : '未就绪' }}
          </span>
        </div>
        <ul class="status-list">
          <li>
            <label>启用状态</label>
            <span>{{ configEnabled ? '已启用' : '未启用' }}</span>
          </li>
          <li>
            <label>配置来源</label>
            <span>{{ sourceLabel }}</span>
          </li>
          <li>
            <label>模型</label>
            <span>{{ currentModel || '未配置' }}</span>
          </li>
          <li>
            <label>接口可用性</label>
            <span>{{ statusAvailable ? '接口正常' : '待检测' }}</span>
          </li>
        </ul>
        <p class="status-desc">{{ statusMessage }}</p>
        <p class="status-timestamp" v-if="lastUpdatedAt">
          最近更新：{{ formatTimestamp(lastUpdatedAt) }}
        </p>
      </div>

      <div class="status-card">
        <div class="card-title">
          <span>提示词绑定</span>
          <span class="status-pill" :class="promptTagClass">
            {{ promptLevelLabel }}
          </span>
        </div>
        <ul class="status-list">
          <li>
            <label>当前账套</label>
            <span>{{ promptScope }}</span>
          </li>
          <li>
            <label>提示词ID</label>
            <span>{{ promptIdText }}</span>
          </li>
          <li>
            <label>绑定人</label>
            <span>{{ promptOwnerName }}</span>
          </li>
          <li>
            <label>行业/来源</label>
            <span>{{ promptSourceLabel }}</span>
          </li>
        </ul>
        <p class="status-desc">{{ promptMessage }}</p>
      </div>
    </div>

    <div class="config-section model-switcher-section" v-if="providers.length > 0">
      <div class="section-header">
        <h3>AI模型切换</h3>
        <span>点击切换当前使用的AI模型</span>
      </div>
      <div class="provider-cards">
        <div
          v-for="p in providers"
          :key="p.provider"
          class="provider-card"
          :class="{ active: p.active, disabled: !p.configured }"
          @click="p.configured && !p.active && switchProvider(p.provider)"
        >
          <div class="provider-icon">{{ p.provider === 'deepseek' ? '🧠' : '🤖' }}</div>
          <div class="provider-info">
            <div class="provider-name">{{ p.name }}</div>
            <div class="provider-model">{{ p.model }}</div>
          </div>
          <div class="provider-status">
            <span v-if="p.active" class="badge active-badge">使用中</span>
            <span v-else-if="p.configured" class="badge ready-badge">可切换</span>
            <span v-else class="badge unconfigured-badge">未配置</span>
          </div>
        </div>
      </div>
      <p v-if="switching" class="switch-hint">正在切换...</p>
    </div>

    <div class="config-section">
      <div class="section-header">
        <h3>AI调用配置</h3>
        <span>{{ config && config.enabled ? '由管理员统一维护' : '未启用' }}</span>
      </div>
      <div class="detail-grid" v-if="config">
        <div class="detail-item">
          <label>API 地址</label>
          <span>{{ config.baseUrl || '未配置' }}</span>
        </div>
        <div class="detail-item">
          <label>默认模型</label>
          <span>{{ config.defaultModel || '未配置' }}</span>
        </div>
        <div class="detail-item">
          <label>API 密钥</label>
          <span>{{ config.apiKey ? config.apiKey : '未配置' }}</span>
        </div>
        <div class="detail-item">
          <label>配置归属</label>
          <span>{{ sourceLabel }}</span>
        </div>
      </div>
      <div class="empty-state" v-else>
        暂无AI配置，请联系管理员分配。
      </div>
    </div>

    <div class="prompt-section">
      <div class="section-header">
        <h3>提示词内容</h3>
        <span v-if="currentAccountSets && currentAccountSets.companyName">
          账套：{{ currentAccountSets.companyName }}
        </span>
      </div>
      <div v-if="loading.prompt" class="empty-state">正在加载提示词...</div>
      <div v-else-if="promptData" class="prompt-content">
        <div class="prompt-block">
          <h4>系统提示词</h4>
          <pre>{{ promptData.systemPrompt }}</pre>
        </div>
        <div class="prompt-block" v-if="promptData.analysisFocus">
          <h4>重点分析方向</h4>
          <pre>{{ promptData.analysisFocus }}</pre>
        </div>
        <div class="prompt-block" v-if="promptData.responseFormat">
          <h4>输出格式要求</h4>
          <pre>{{ promptData.responseFormat }}</pre>
        </div>
        <div class="prompt-block" v-if="promptData.fewShotExamples">
          <h4>示例</h4>
          <pre>{{ promptData.fewShotExamples }}</pre>
        </div>
      </div>
      <div v-else class="empty-state">
        {{ promptError || '暂无提示词配置，请联系管理员或会计主管配置。' }}
      </div>
    </div>

    <div class="tips-card">
      <h4>协同说明</h4>
      <ul>
        <li>AI接口、密钥与模型由管理员在管理端统一维护，如需调整请联系管理员。</li>
        <li>会计师可在管理员端维护提示词模板并绑定到账套，修改后会在此实时生效。</li>
        <li>如遇到提示词内容与业务不符，可在管理员端为所在账套更新绑定关系。</li>
      </ul>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'AiConfigSimple',
  data() {
    return {
      config: null,
      status: null,
      promptData: null,
      promptMeta: {},
      statusMessage: '正在获取AI状态...',
      promptError: '',
      lastUpdatedAt: null,
      providers: [],
      switching: false,
      loading: {
        config: false,
        prompt: false
      }
    }
  },
  computed: {
    ...mapState(['currentAccountSets', 'User']),
    isRefreshing() {
      return this.loading.config || this.loading.prompt
    },
    configEnabled() {
      return !!(this.config && this.config.enabled)
    },
    currentModel() {
      if (this.status && this.status.model) {
        return this.status.model
      }
      return this.config ? this.config.defaultModel : ''
    },
    statusAvailable() {
      return !!(this.status && this.status.available)
    },
    aiConnected() {
      return this.status && this.status.enabled && this.status.hasValidConfig && this.status.available
    },
    sourceLabel() {
      if (!this.status) {
        return '系统'
      }
      return this.status.configSource === 'user' ? '管理员分配的专属配置' : '系统默认配置'
    },
    promptLevelLabel() {
      const level = (this.promptData && this.promptData.promptLevel) || 'default'
      switch (level) {
        case 'binding':
          return '账套自定义'
        case 'user':
          return '会计师自定义'
        case 'industry':
          return '行业模板'
        default:
          return '系统默认'
      }
    },
    promptTagClass() {
      const level = (this.promptData && this.promptData.promptLevel) || 'default'
      return {
        binding: 'online',
        user: 'online',
        industry: 'warning',
        default: 'offline'
      }[level] || 'offline'
    },
    promptIdText() {
      if (this.promptData && this.promptData.promptId) {
        return this.promptData.promptId
      }
      return '—'
    },
    promptOwnerName() {
      if (this.promptMeta && this.promptMeta.userName) {
        return this.promptMeta.userName
      }
      if (this.User && this.User.realName) {
        return this.User.realName
      }
      return '—'
    },
    promptScope() {
      if (this.currentAccountSets && this.currentAccountSets.companyName) {
        return `${this.currentAccountSets.companyName}`
      }
      return '未选择账套'
    },
    promptMessage() {
      if (this.promptError) {
        return this.promptError
      }
      if (this.promptData) {
        return '提示词已就绪，可直接用于AI自动科目匹配。'
      }
      return '暂无提示词配置，请在管理员端完成绑定。'
    },
    promptSourceLabel() {
      if (!this.promptData) {
        return '—'
      }
      if (this.promptData.promptLevel === 'binding') {
        return '账套+会计师绑定'
      }
      if (this.promptData.promptLevel === 'user') {
        return '会计师专属'
      }
      if (this.promptData.promptLevel === 'industry') {
        return '行业模板'
      }
      return '系统默认'
    }
  },
  mounted() {
    this.refreshAll()
  },
  watch: {
    currentAccountSets: {
      handler(newVal, oldVal) {
        if (!newVal) {
          this.promptData = null
          return
        }
        const newId = newVal && newVal.id
        const oldId = oldVal && oldVal.id
        if (newId && newId !== oldId) {
          this.loadPrompt()
        }
      },
      deep: false
    }
  },
  methods: {
    async refreshAll() {
      await Promise.all([this.loadConfig(), this.loadStatus(), this.loadPrompt(), this.loadProviders()])
    },
    async loadProviders() {
      try {
        const response = await fetch('/api/ai-config/providers', { credentials: 'include' })
        const result = await response.json()
        if (result.success) {
          this.providers = result.data || []
        }
      } catch (error) {
        console.error('加载Provider列表失败', error)
      }
    },
    async switchProvider(provider) {
      this.switching = true
      try {
        const response = await fetch('/api/ai-config/switch-provider', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ provider })
        })
        const result = await response.json()
        if (result.success) {
          await this.refreshAll()
        } else {
          alert(result.message || '切换失败')
        }
      } catch (error) {
        console.error('切换模型失败', error)
        alert('切换模型失败')
      } finally {
        this.switching = false
      }
    },
    async loadConfig() {
      this.loading.config = true
      try {
        const response = await fetch('/api/ai-config/get', { credentials: 'include' })
        const result = await response.json()
        if (result.success) {
          this.config = result.data || {}
          this.lastUpdatedAt = Date.now()
        } else {
          this.config = null
          this.statusMessage = result.message || '无法获取AI配置'
        }
      } catch (error) {
        console.error('加载AI配置失败', error)
        this.config = null
        this.statusMessage = '加载AI配置时出现异常'
      } finally {
        this.loading.config = false
      }
    },
    async loadStatus() {
      try {
        const response = await fetch('/api/ai-config/status', { credentials: 'include' })
        const result = await response.json()
        if (result.success) {
          this.status = result.data || {}
          this.statusMessage = this.buildStatusMessage(this.status)
        } else {
          this.status = null
          this.statusMessage = result.message || '无法获取AI状态'
        }
      } catch (error) {
        console.error('加载AI状态失败', error)
        this.status = null
        this.statusMessage = '加载AI状态时出现异常'
      }
    },
    async loadPrompt() {
      if (!this.currentAccountSets || !this.currentAccountSets.id) {
        this.promptData = null
        this.promptError = '尚未选择账套'
        return
      }
      this.loading.prompt = true
      try {
        const response = await fetch(`/api/ai-config/prompt?accountSetsId=${this.currentAccountSets.id}`, {
          credentials: 'include'
        })
        const result = await response.json()
        if (result.success) {
          const payload = result.data || {}
          this.promptData = payload.prompt || null
          this.promptMeta = payload
          this.promptError = ''
        } else {
          this.promptData = null
          this.promptMeta = {}
          this.promptError = result.message || '无法获取提示词配置'
        }
      } catch (error) {
        console.error('加载提示词失败', error)
        this.promptData = null
        this.promptMeta = {}
        this.promptError = '加载提示词时出现异常'
      } finally {
        this.loading.prompt = false
      }
    },
    buildStatusMessage(status) {
      if (!status || !status.enabled) {
        return 'AI功能未启用，请联系管理员分配AI配置。'
      }
      if (!status.hasValidConfig) {
        return 'AI配置不完整，请管理员在后台补充接口信息。'
      }
      if (!status.available) {
        return 'AI服务暂未连通，可在管理员端执行连接检测。'
      }
      return `AI服务已就绪，当前模型：${status.model || '未设置'}`
    },
    formatTimestamp(timestamp) {
      if (!timestamp) {
        return '--'
      }
      const date = new Date(timestamp)
      return date.toLocaleString()
    }
  }
}
</script>

<style lang="less" scoped>
.ai-config-simple {
  padding: 24px;
  max-width: 960px;
  margin: 0 auto;
  color: #303133;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  h2 {
    margin: 0 0 6px;
    font-size: 24px;
    color: #1f2d3d;
  }

  p {
    margin: 0;
    color: #909399;
  }
}

.header-actions {
  display: flex;
  gap: 12px;
}

.refresh-btn {
  border: 1px solid #409eff;
  background: #409eff;
  color: #fff;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  transition: opacity 0.2s;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
}

.status-card {
  background: #fff;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.06);
}

.card-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  margin-bottom: 12px;
}

.status-pill {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  color: #fff;

  &.online {
    background: #67c23a;
  }

  &.offline {
    background: #f56c6c;
  }

  &.warning {
    background: #e6a23c;
  }
}

.status-list {
  list-style: none;
  padding: 0;
  margin: 0 0 10px;

  li {
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    padding: 6px 0;
    border-bottom: 1px dashed #ebeef5;

    label {
      color: #909399;
    }
  }
}

.status-desc {
  font-size: 13px;
  color: #606266;
  margin: 0;
}

.status-timestamp {
  margin-top: 8px;
  font-size: 12px;
  color: #c0c4cc;
}

.config-section,
.prompt-section,
.tips-card {
  background: #fff;
  border-radius: 10px;
  padding: 20px;
  margin-top: 20px;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.06);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  color: #909399;

  h3 {
    margin: 0;
    color: #303133;
  }
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  padding: 12px;
  background: #f8f9fb;
  border-radius: 8px;

  label {
    font-size: 12px;
    color: #909399;
    margin-bottom: 4px;
  }

  span {
    font-size: 14px;
    color: #303133;
    word-break: break-all;
  }
}

.prompt-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.prompt-block {
  background: #f5f7fa;
  border-radius: 8px;
  padding: 12px 16px;

  h4 {
    margin: 0 0 8px;
    font-size: 14px;
    color: #606266;
  }

  pre {
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 13px;
    line-height: 1.6;
    color: #303133;
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace;
  }
}

.model-switcher-section {
  .provider-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 16px;
  }

  .provider-card {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px;
    border-radius: 10px;
    border: 2px solid #e4e7ed;
    background: #fff;
    cursor: pointer;
    transition: all 0.2s;

    &:hover:not(.disabled):not(.active) {
      border-color: #409eff;
      box-shadow: 0 4px 12px rgba(64, 158, 255, 0.15);
    }

    &.active {
      border-color: #67c23a;
      background: #f0f9eb;
      cursor: default;
    }

    &.disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .provider-icon {
    font-size: 28px;
    flex-shrink: 0;
  }

  .provider-info {
    flex: 1;
    min-width: 0;
  }

  .provider-name {
    font-size: 15px;
    font-weight: 600;
    color: #303133;
  }

  .provider-model {
    font-size: 12px;
    color: #909399;
    margin-top: 2px;
  }

  .badge {
    font-size: 12px;
    padding: 3px 10px;
    border-radius: 12px;
    white-space: nowrap;
  }

  .active-badge {
    background: #67c23a;
    color: #fff;
  }

  .ready-badge {
    background: #e6f1fc;
    color: #409eff;
  }

  .unconfigured-badge {
    background: #f4f4f5;
    color: #909399;
  }

  .switch-hint {
    text-align: center;
    margin-top: 10px;
    font-size: 13px;
    color: #909399;
  }
}

.empty-state {
  text-align: center;
  padding: 24px 0;
  color: #a0a3ab;
  background: #f9fafc;
  border-radius: 8px;
}

.tips-card ul {
  margin: 10px 0 0;
  padding-left: 16px;
  color: #606266;

  li {
    margin-bottom: 6px;
  }
}

@media (max-width: 768px) {
  .ai-config-simple {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;

    .header-actions {
      margin-top: 12px;
    }
  }
}
</style>
